import React from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';

const FeedbackDetails = ({ feedback, structuredFeedback, getUserInfo }) => {
  const parseDetailedFeedback = (text) => {
    if (!text) return null;
    const parts = text.split(' | ');
    const result = { summary: '', fullAvailabilityMembers: [], timeSlots: [], bestOverlap: null };
    parts.forEach((part) => {
      if (part.includes('members have availability but no overlapping time')) {
        result.summary = part;
      } else if (part.startsWith('Full availability members:')) {
        const membersStr = part.replace('Full availability members: ', '');
        result.fullAvailabilityMembers = membersStr
          .replace(/[[\]]/g, '')
          .split(',')
          .map((id) => parseInt(id.trim()));
      } else if (part.startsWith('Time slots:')) {
        const slotsStr = part.replace('Time slots: ', '');
        result.timeSlots = slotsStr
          .split('; ')
          .map((slot) => {
            const match = slot.match(/Member (\d+): (.+)/);
            if (match) {
              return { memberId: parseInt(match[1]), timeRanges: match[2].split(', ') };
            }
            return null;
          })
          .filter(Boolean);
      } else if (part.startsWith('Best available overlap:')) {
        const overlapStr = part.replace('Best available overlap: ', '');
        const match = overlapStr.match(/(\d+) members at (.+)/);
        if (match) {
          const timeMatch = match[2].match(/(.+) - (.+)/);
          if (timeMatch) {
            result.bestOverlap = {
              memberCount: parseInt(match[1]),
              startTime: timeMatch[1],
              endTime: timeMatch[2],
            };
          }
        }
      }
    });
    return result;
  };

  if (structuredFeedback) {
    return (
      <div className="space-y-3">
        {structuredFeedback.full_availability_members && structuredFeedback.full_availability_members.length > 0 && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3">
            <div className="flex items-center space-x-2">
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium text-green-800">Full Availability Members</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {structuredFeedback.full_availability_members.map((memberId, index) => {
                const userInfo = getUserInfo ? getUserInfo(memberId) : null;
                const displayName = userInfo ? `${userInfo.name} (${userInfo.email})` : `User ${memberId}`;
                return (
                  <span key={index} className="inline-flex items-center rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                    {displayName}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        {structuredFeedback.time_slots && structuredFeedback.time_slots.length > 0 && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-center space-x-2">
              <ClockIcon className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium text-blue-800">Member Availability Time Slots</span>
            </div>
            <div className="mt-2 space-y-2">
              {structuredFeedback.time_slots.map((slot, index) => {
                const userInfo = getUserInfo ? getUserInfo(slot.member_id) : null;
                const displayName = userInfo ? `${userInfo.name}` : `User ${slot.member_id}`;
                return (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-800">{displayName}</span>
                    <div className="flex flex-wrap gap-1">
                      {slot.time_ranges.map((timeRange, timeIndex) => (
                        <span key={timeIndex} className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">
                          {timeRange.display}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {structuredFeedback.best_overlap && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
              <span className="text-sm font-medium text-yellow-800">Best Available Overlap</span>
            </div>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                {structuredFeedback.best_overlap.member_count} members can be available together from{' '}
                <span className="font-medium">{structuredFeedback.best_overlap.start_time}</span> to{' '}
                <span className="font-medium">{structuredFeedback.best_overlap.end_time}</span>
              </p>
              <p className="mt-1 text-xs text-yellow-600">This is the best partial overlap available, but not enough for the full crew.</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  const parsed = parseDetailedFeedback(feedback);
  if (!parsed) return <span className="text-sm text-gray-600">{feedback}</span>;

  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-600">{parsed.summary}</div>
      {parsed.fullAvailabilityMembers.length > 0 && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
          <div className="flex items-center space-x-2">
            <CheckCircleIcon className="h-4 w-4 text-green-500" />
            <span className="text-sm font-medium text-green-800">Full Availability Members</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {parsed.fullAvailabilityMembers.map((memberId, index) => {
              const userInfo = getUserInfo ? getUserInfo(memberId) : null;
              const displayName = userInfo ? `${userInfo.name} (${userInfo.email})` : `User ${memberId}`;
              return (
                <span key={index} className="inline-flex items-center rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                  {displayName}
                </span>
              );
            })}
          </div>
        </div>
      )}
      {parsed.timeSlots.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-center space-x-2">
            <ClockIcon className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium text-blue-800">Member Availability Time Slots</span>
          </div>
          <div className="mt-2 space-y-2">
            {parsed.timeSlots.map((slot, index) => {
              const userInfo = getUserInfo ? getUserInfo(slot.memberId) : null;
              const displayName = userInfo ? `${userInfo.name} (${userInfo.email})` : `User ${slot.memberId}`;
              return (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-800">{displayName}</span>
                  <div className="flex flex-wrap gap-1">
                    {slot.timeRanges.map((timeRange, timeIndex) => (
                      <span key={timeIndex} className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">
                        {timeRange}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {parsed.bestOverlap && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
          <div className="flex items-center space-x-2">
            <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
            <span className="text-sm font-medium text-yellow-800">Best Available Overlap</span>
          </div>
          <div className="mt-2 text-sm text-yellow-700">
            <p>
              {parsed.bestOverlap.memberCount} members can be available together from{' '}
              <span className="font-medium">{parsed.bestOverlap.startTime}</span> to{' '}
              <span className="font-medium">{parsed.bestOverlap.endTime}</span>
            </p>
            <p className="mt-1 text-xs text-yellow-600">This is the best partial overlap available, but not enough for the full crew.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackDetails;



